import React from "react";
import {
  HashRouter as Router,
  Route,
  Switch,
  NavLink,
  Redirect,
} from "react-router-dom";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileAlt, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import withTracker from "./withTracker";
import CandidateInfo from "../CandidateProfile/CandidateInfo";
import CandidateProfileMessages from "../CandidateProfile/CandidateProfileMessages";
import messageType from "../CandidateProfile/types/messageType";
import otherUserType from "../CandidateProfile/types/otherUserType";
import candidateAboutType from "../CandidateProfile/types/candidateAboutType";
import {
  RouterNav,
  RouterList,
  RouterItem,
  TabIcon,
  TabText,
} from "./styles/router-nav-styles";

// set a custom tracking title here as the actual page title contains
// PII, which we do not want to send to google analytics
const customTrackingOpts = {
  title: "[FILTERED_CANDIDATE_NAME] | The Hiring Hub",
};

const TrackedInfo = withTracker(CandidateInfo, customTrackingOpts);
const TrackedMessages = withTracker(
  CandidateProfileMessages,
  customTrackingOpts
);
const CandidatePageTabNavigation = (props) => {
  const {
    updateStatusLink,
    updateCandidateStatus,
    toggleActionsDropdown,
    actionsDropdown,
    isEmployer,
    isRejected,
    interviewConfirmed,
    lastAction,
    jobCurrencySymbol,
    jobStatus,
    candidateAbout,
    cv,
    cvCount,
    otherUser,
    candidateDescription,
    offerMade,
    jobFilled,
    otherUserName,
    messages,
    userId,
    scrollToBottom,
    candidateProfileRoutes,
    messageTabTitle,
    increaseApplicationCvReadCount,
    handleDownloadCv,
    emailDomains,
    applicationId,
    candidateId,
    showShareButton,
  } = props;

  const CandidateInfoPage = (compProps) => (
    <TrackedInfo
      {...compProps}
      candidateAbout={candidateAbout}
      candidateDescription={candidateDescription}
      cv={cv}
      cvCount={cvCount}
      isEmployer={isEmployer}
      interviewConfirmed={interviewConfirmed}
      jobStatus={jobStatus}
      lastAction={lastAction}
      otherUser={otherUser}
      increaseApplicationCvReadCount={increaseApplicationCvReadCount}
      handleDownloadCv={handleDownloadCv}
      emailDomains={emailDomains}
      applicationId={applicationId}
      candidateId={candidateId}
      showShareButton={showShareButton}
      jobCurrencySymbol={jobCurrencySymbol}
    />
  );

  const CandidateMessagePage = (compProps) => (
    <TrackedMessages
      {...compProps}
      actionsDropdown={actionsDropdown}
      interviewConfirmed={interviewConfirmed}
      isEmployer={isEmployer}
      isRejected={isRejected}
      jobCurrencySymbol={jobCurrencySymbol}
      jobStatus={jobStatus}
      lastAction={lastAction}
      messages={messages}
      offerMade={offerMade}
      jobFilled={jobFilled}
      otherUserName={otherUserName}
      scrollToBottom={scrollToBottom}
      toggleActionsDropdown={toggleActionsDropdown}
      updateCandidateStatus={updateCandidateStatus}
      updateStatusLink={updateStatusLink}
      userId={userId}
    />
  );

  return (
    <Router>
      <RouterNav id="tab-nav">
        <RouterList>
          <RouterItem>
            <NavLink
              to={candidateProfileRoutes.information}
              activeClassName="active"
            >
              <TabIcon>
                <FontAwesomeIcon icon={faFileAlt} />
              </TabIcon>
              <TabText>CV/Information</TabText>
            </NavLink>
          </RouterItem>
          <RouterItem>
            <NavLink
              to={candidateProfileRoutes.messages}
              data-test-id="messages-tab-link"
            >
              <TabIcon>
                <FontAwesomeIcon icon={faEnvelope} />
              </TabIcon>
              <TabText>{messageTabTitle}</TabText>
            </NavLink>
          </RouterItem>
        </RouterList>
      </RouterNav>
      <Switch>
        <Route
          exact
          path={candidateProfileRoutes.information}
          render={CandidateInfoPage}
        />
        <Route
          exact
          path={candidateProfileRoutes.messages}
          render={CandidateMessagePage}
        />
        <Route
          exact
          path="/"
          render={() => (
            <Redirect from="/" to={candidateProfileRoutes.information} />
          )}
        />
      </Switch>
    </Router>
  );
};

CandidatePageTabNavigation.propTypes = {
  actionsDropdown: PropTypes.bool.isRequired,
  candidateAbout: candidateAboutType.isRequired,
  candidateDescription: PropTypes.string.isRequired,
  candidateProfileRoutes: PropTypes.shape({
    information: PropTypes.string,
    messages: PropTypes.string,
  }).isRequired,
  cv: PropTypes.string.isRequired,
  interviewConfirmed: PropTypes.bool.isRequired,
  isEmployer: PropTypes.bool.isRequired,
  isRejected: PropTypes.bool.isRequired,
  messageTabTitle: PropTypes.string.isRequired,
  messages: PropTypes.arrayOf(messageType).isRequired,
  jobCurrencySymbol: PropTypes.string.isRequired,
  jobStatus: PropTypes.string.isRequired,
  lastAction: PropTypes.string,
  jobFilled: PropTypes.bool,
  offerMade: PropTypes.bool.isRequired,
  otherUser: otherUserType.isRequired,
  otherUserName: PropTypes.string.isRequired,
  scrollToBottom: PropTypes.func.isRequired,
  toggleActionsDropdown: PropTypes.func.isRequired,
  updateCandidateStatus: PropTypes.func.isRequired,
  userId: PropTypes.number.isRequired,
  updateStatusLink: PropTypes.string.isRequired,
  cvCount: PropTypes.shape({
    cvOpenedCount: PropTypes.number,
    cvDownloadedCount: PropTypes.number,
  }),
  increaseApplicationCvReadCount: PropTypes.func.isRequired,
  handleDownloadCv: PropTypes.func.isRequired,
  emailDomains: PropTypes.arrayOf(PropTypes.string),
  candidateId: PropTypes.number.isRequired,
  applicationId: PropTypes.number.isRequired,
  showShareButton: PropTypes.bool.isRequired,
};

CandidatePageTabNavigation.defaultProps = {
  jobFilled: PropTypes.isNull,
  lastAction: PropTypes.isNull,
  cvCount: PropTypes.isNull,
  emailDomains: PropTypes.isNull,
};

export default CandidatePageTabNavigation;
